import React from "react";
import PopupDialog from "components/dialog/PopupDialog";
import useAxios from "../../../hooks/useAxios";
import useAxiosFunction from "../../../hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import {
  listItems,
  showEmpty,
  SystemTableCard,
} from "../system/SystemPageUtils";
import { formatPersonName } from "features/kms/common/nameFormat";

function titleFromStatus(status) {
  switch (status) {
    case "DIAJUKAN":
      return "Pengajuan Baru";
    case "PERLU_PERBAIKAN":
      return "Perlu Perbaikan";
    case "TERVERIFIKASI":
      return "Terverifikasi";
    case "DITOLAK":
      return "Ditolak";
    default:
      return "Riwayat Verifikasi";
  }
}

function isVerificationClosed(statusCode) {
  const code = String(statusCode || "").toUpperCase();
  return code === "DITOLAK" || code.startsWith("TERVERIFIKASI");
}

function formatScopeLabel(item) {
  if (String(item?.entity_code || "").toUpperCase() !== "PERSONAL") {
    return item?.scope_label || item?.entity_id;
  }
  return formatPersonName(
    {
      nama_lengkap: item?.scope_label,
      gelar_depan: item?.scope_gelar_depan,
      gelar_belakang: item?.scope_gelar_belakang,
    },
    ["nama_lengkap"],
  );
}

export default function VerificationBoard({ status }) {
  const [resp, error, loading, refetch] = useAxios({
    method: "GET",
    url: `/verifications/queue${status ? `?status=${status}` : ""}`,
    auth: true,
  });
  const [, actionError, actionLoading, axiosFetch] = useAxiosFunction();
  const [showMsg, confirmMsg] = useMsgBox();
  const [pendingAction, setPendingAction] = React.useState(null);
  const [actionNote, setActionNote] = React.useState("");

  const items = listItems(resp);

  const handleAction = async (id, action) => {
    const ok = await confirmMsg(
      "Konfirmasi",
      `Apakah Anda yakin akan menjalankan aksi ${action}?`,
    );
    if (!ok) return;
    setPendingAction({ id, action });
    setActionNote("");
  };

  const closeActionDialog = () => {
    if (actionLoading) return;
    setPendingAction(null);
    setActionNote("");
  };

  const submitAction = async () => {
    if (!pendingAction?.id || !pendingAction?.action) {
      return;
    }

    const actionResp = await axiosFetch({
      method: "POST",
      url: `/verifications/${pendingAction.id}/action`,
      auth: true,
      data: { action: pendingAction.action, catatan: actionNote },
    });

    if (actionResp?.data?.success === true) {
      closeActionDialog();
      await showMsg("Informasi", "Proses verifikasi berhasil disimpan.");
      refetch();
    } else if (actionResp?.message) {
      await showMsg("Error", actionResp.message);
    }
  };
  const columns = [
    "no",
    "verification_name",
    "entity_name",
    "status_name",
    "submitted_at",
    "verified_at",
    "action",
  ];
  const coltype = {
    no: { label: "No", width: 50, align: "center" },
    verification_name: { label: "Jenis Verifikasi" },
    entity_name: {
      label: "Entitas",
      renderFunc: (val, col, item) =>
        `${showEmpty(val)} / ${showEmpty(formatScopeLabel(item))}`,
    },
    status_name: {
      label: "Status",
      align: "center",
      renderFunc: (val) => showEmpty(val),
    },
    submitted_at: { label: "Diajukan", renderFunc: (val) => showEmpty(val) },
    verified_at: { label: "Diverifikasi", renderFunc: (val) => showEmpty(val) },
    action: {
      label: "Aksi",
      align: "center",
      renderFunc: (val, col, item) => (
        <>
          {!isVerificationClosed(item.status_code) ? (
            <>
              <a
                href="#"
                className="btn btn-success btn-xs"
                onClick={(event) => {
                  event.preventDefault();
                  handleAction(item.id_verifikasi, "VERIFY");
                }}
                title="Verify"
              >
                <i className="fa fa-check" />
              </a>
              &nbsp;
              <a
                href="#"
                className="btn btn-warning btn-xs"
                onClick={(event) => {
                  event.preventDefault();
                  handleAction(item.id_verifikasi, "REQUEST_REVISION");
                }}
                title="Request Revision"
              >
                <i className="fa fa-edit" />
              </a>
              &nbsp;
              <a
                href="#"
                className="btn btn-danger btn-xs"
                onClick={(event) => {
                  event.preventDefault();
                  handleAction(item.id_verifikasi, "REJECT");
                }}
                title="Reject"
              >
                <i className="fa fa-times" />
              </a>
            </>
          ) : (
            <span className="badge badge-secondary">SELESAI</span>
          )}
        </>
      ),
    },
  };

  return (
    <SystemTableCard
      title={titleFromStatus(status)}
      description={
        actionLoading
          ? "Memproses aksi verifikasi..."
          : "Daftar antrian dan riwayat verifikasi."
      }
      data={items}
      dlist={{ columns, sort_by: [] }}
      paging={{ page: 1, perpage: Math.max(items.length || 1, 10) }}
      coltype={coltype}
      loading={loading}
      error={error ? `Gagal memuat verifikasi: ${error}` : actionError}
    >
      <PopupDialog
        title={`Catatan ${pendingAction?.action || ""}`}
        size="md"
        show={Boolean(pendingAction)}
        closeHandle={closeActionDialog}
        allowClose={true}
        closeLabel="Batal"
        doHandle={submitAction}
        doLabel={actionLoading ? "Menyimpan..." : "Simpan"}
        doDisabled={actionLoading}
      >
        <div className="form-group mb-0">
          <label htmlFor="verification-action-note">Catatan</label>
          <textarea
            id="verification-action-note"
            className="form-control"
            rows={4}
            value={actionNote}
            onChange={(event) => setActionNote(event.target.value)}
            placeholder="Tulis catatan verifikasi jika diperlukan"
          />
        </div>
      </PopupDialog>
    </SystemTableCard>
  );
}
